/**
 * Каталог сантехники: то, что стоит в санузле и ванной. Навесное — умывальник и
 * полотенцесушитель — ставится на стену; унитаз, ванна, душ и техника — на пол.
 * Габариты — типовые, цвета условные: сантехника белая, техника серая.
 */
import { LayerKind } from "@/shared/plan/layerKind";
import { CatalogAttach, type CatalogSection } from "../../types";
import { PlumbingId } from "./ids";
import type { PlumbingCatalogItem } from "./types";

/** Ванны бывают от сидячих 1200 до 1900 мм — растянутая модель в этих пределах выглядит естественно. */
const BATH_LIMITS = {
	width: { min: 1200, max: 1900 },
	depth: { min: 650, max: 900 },
};

const CERAMIC = "#dbe7f0";
const APPLIANCE = "#b8c2cf";

/** Напольный прибор с моделью-файлом `public/models/plumbing/<id>.glb`: габарит модели — габарит предмета. */
const glb = (
	fields: Omit<PlumbingCatalogItem, "view3d" | "attach" | "elevation"> & { elevation?: number },
): PlumbingCatalogItem => ({
	...fields,
	elevation: fields.elevation ?? 0,
	attach: CatalogAttach.Everywhere,
	view3d: {
		type: "model",
		url: `/models/plumbing/${fields.id}.glb`,
		width: fields.width,
		height: fields.height,
		depth: fields.depth,
		color: fields.color,
	},
});

export const plumbingSection: CatalogSection<PlumbingCatalogItem> = {
	kind: LayerKind.Plumbing,
	label: "Сантехника",
	canChangeDimensions: true,
	subGroups: ["Санузел", "Ванная", "Техника"],
	families: [
		{ id: "toilet", name: "Унитаз", defaultItem: "toilet" },
		{ id: "vanity", name: "Умывальник с тумбой", defaultItem: "pl-330" },
		{ id: "bath", name: "Ванна", defaultItem: "bath" },
		{ id: "sink", name: "Раковина накладная", defaultItem: "sink-countertop" },
	],
	items: [
		{
			id: "pl-330",
			family: "vanity",
			subGroup: "Санузел",
			name: "Умывальник с тумбой без ручек",
			description:
				"Подвесная закрытая тумба 600 мм с ящиком без ручек, керамическая раковина-столешница и хромированный смеситель. Верх раковины на 850 мм от пола.",
			width: 600,
			height: 700,
			depth: 460,
			elevation: 700,
			color: "#3b3e44",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "vanity-wall",
				width: 600,
				height: 700,
				depth: 460,
				color: "#3b3e44",
				tintable: true,
			},
		},
		{
			id: "toilet",
			family: "toilet",
			subGroup: "Санузел",
			name: "Унитаз",
			description: "Напольный унитаз с бачком, ставится вплотную к стене.",
			width: 370,
			height: 800,
			depth: 650,
			elevation: 0,
			color: CERAMIC,
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "model",
				url: "/models/plumbing/toilet.glb",
				width: 370,
				height: 800,
				depth: 650,
				color: CERAMIC,
				roughness: 0.2,
			},
		},
		{
			id: "towel-dryer",
			subGroup: "Санузел",
			name: "Полотенцесушитель",
			description:
				"Водяная лесенка из хромированной трубы, восемь перекладин, низ на 900 мм от пола.",
			width: 500,
			height: 800,
			depth: 100,
			elevation: 900,
			color: "#94a3b8",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "component",
				id: "towel-dryer-ladder",
				width: 500,
				height: 800,
				depth: 100,
				color: "#d9dde2",
				tintable: true,
			},
		},
		{
			id: "bath",
			family: "bath",
			sizeLimits: BATH_LIMITS,
			subGroup: "Ванная",
			name: "Ванна встроенная",
			description: "Ванна 1700 × 750, борт на 600 мм от пола.",
			width: 1700,
			height: 600,
			depth: 750,
			elevation: 0,
			color: CERAMIC,
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "model",
				url: "/models/plumbing/bath.glb",
				width: 1700,
				height: 600,
				depth: 750,
				color: CERAMIC,
				roughness: 0.2,
			},
		},
		{
			id: "shower",
			subGroup: "Ванная",
			name: "Душевая кабина",
			description:
				"Кабина 900 × 900: стеклянные стенки в чёрной раме, низкий поддон, тропический душ.",
			width: 900,
			height: 2000,
			depth: 900,
			elevation: 0,
			color: "#bcd8ea",
			attach: CatalogAttach.Everywhere,
			// Модель квадратная в плане, но ниже: стеклянный короб растяжение по высоте переносит
			view3d: {
				type: "model",
				url: "/models/plumbing/shower.glb",
				width: 900,
				height: 2000,
				depth: 900,
				color: "#bcd8ea",
			},
		},
		{
			id: "washer",
			subGroup: "Техника",
			name: "Стиральная машина",
			description:
				"Фронтальная загрузка 600 × 600: круглый люк с тонированным стеклом, панель с дисплеем.",
			width: 600,
			height: 850,
			depth: 600,
			elevation: 0,
			color: APPLIANCE,
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "model",
				url: "/models/plumbing/washer.glb",
				width: 600,
				height: 850,
				depth: 600,
				color: APPLIANCE,
				// Люк модели смотрит в −Z, а перед символа — к низу плана
				turn: 180,
			},
		},
		{
			id: "vanity-vessel",
			family: "vanity",
			subGroup: "Санузел",
			name: "Умывальник с тумбой, раковина-чаша",
			description:
				"Подвесная дубовая тумба 600 мм с накладной круглой раковиной и чёрным смесителем. Край чаши — около 850 мм от пола.",
			width: 600,
			height: 545,
			depth: 515,
			// Высота у настенного — центр: чаша и смеситель выше тумбы, край чаши выходит на ~850
			elevation: 740,
			color: "#b98a52",
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "model",
				url: "/models/plumbing/vanity-vessel.glb",
				width: 600,
				height: 545,
				depth: 515,
				color: "#b98a52",
			},
		},
		{
			id: "bidet",
			subGroup: "Санузел",
			name: "Биде",
			description: "Керамическое биде 520 × 550, высота 450.",
			width: 520,
			height: 450,
			depth: 550,
			elevation: 0,
			color: CERAMIC,
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "model",
				url: "/models/plumbing/bidet.glb",
				width: 520,
				height: 450,
				depth: 550,
				color: CERAMIC,
				roughness: 0.2,
			},
		},
		{
			id: "bath-freestanding",
			family: "bath",
			subGroup: "Ванная",
			name: "Ванна отдельностоящая",
			description:
				"Овальная ванна 1600 × 1450 вместе с напольным смесителем за ней; высота по смесителю 910.",
			width: 1600,
			height: 910,
			depth: 1450,
			elevation: 0,
			color: CERAMIC,
			attach: CatalogAttach.Everywhere,
			view3d: {
				type: "model",
				url: "/models/plumbing/bath-freestanding.glb",
				width: 1600,
				height: 910,
				depth: 1450,
				color: CERAMIC,
				roughness: 0.2,
			},
		},
		// Пятая партия: цвет модели — из её текстуры, размеры по пропорциям модели, тумба — по жизни.
		// Накладная раковина стоит на столешнице: низ на 850 мм.
		glb({
			id: PlumbingId.ToiletModern,
			family: "toilet",
			subGroup: "Санузел",
			name: "Унитаз современный",
			description: "Напольный унитаз 450 × 685 с прямым бачком, высота 780.",
			width: 450,
			height: 780,
			depth: 685,
			color: "#d9d2c1",
		}),
		glb({
			id: PlumbingId.ToiletRound,
			family: "toilet",
			subGroup: "Санузел",
			name: "Унитаз округлый",
			description: "Напольный унитаз 380 × 720 со скруглённым бачком, высота 720.",
			width: 380,
			height: 720,
			depth: 720,
			color: "#d1d1cf",
		}),
		glb({
			id: PlumbingId.VanityCabinetWood,
			family: "vanity",
			subGroup: "Санузел",
			name: "Тумба под раковину напольная, дерево",
			description: "Напольная деревянная тумба 800 × 500 со встроенной раковиной, верх на 850 мм.",
			width: 800,
			height: 850,
			depth: 500,
			color: "#9b8778",
		}),
		glb({
			id: PlumbingId.SinkCountertop,
			family: "sink",
			round: true,
			subGroup: "Санузел",
			name: "Раковина-чаша со смесителем",
			description: "Круглая накладная раковина ⌀ 450 со смесителем, стоит на столешнице.",
			width: 450,
			height: 250,
			depth: 450,
			elevation: 850,
			color: "#7b6f5e",
		}),
		glb({
			id: PlumbingId.SinkCurved,
			family: "sink",
			subGroup: "Санузел",
			name: "Раковина овальная",
			description: "Овальная накладная раковина 600 × 450, стоит на столешнице.",
			width: 600,
			height: 250,
			depth: 450,
			elevation: 850,
			color: "#d1ccba",
		}),
		glb({
			id: PlumbingId.LaundryHamper,
			round: true,
			subGroup: "Санузел",
			name: "Корзина для белья с крышкой",
			description: "Плетёная корзина ⌀ 550 с крышкой, высота 440.",
			width: 550,
			height: 440,
			depth: 550,
			color: "#836d4b",
		}),
	],
};
